/**
 * CalendarTaskCard
 * Visual representation of a Task (delivery/deadline)
 * Distinct from EventCard with dashed border
 */

import React from 'react'
import { CheckSquare, AlertCircle, FileText, Clock } from 'lucide-react'
import type { CalendarItem } from '../../types/calendar'
import { CopyableProcessNumber } from '../ui/CopyableProcessNumber'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/utils/cn'
import { CalendarPriorityFlag, taskCategoryLabels, taskStatusLabels } from './CalendarBadges'

interface CalendarTaskCardProps {
  item: CalendarItem
  onClick?: () => void
  className?: string
}

/**
 * Check if task is overdue
 */
const isOverdue = (item: CalendarItem): boolean => {
  if (item.taskStatus === 'COMPLETED' || item.taskStatus === 'CANCELLED') {
    return false
  }

  const dueDate = new Date(item.date)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  dueDate.setHours(0, 0, 0, 0)

  return dueDate < today
}

/**
 * Borda tracejada = TAREFA. Atrasada usa o destrutivo; prazo processual o
 * primário do tema; concluída/cancelada ficam esmaecidas.
 */
const getTaskToneClasses = (item: CalendarItem) => {
  if (isOverdue(item)) return 'border-destructive/60 bg-card text-foreground'
  if (item.taskStatus === 'COMPLETED') return 'border-border bg-card text-muted-foreground'
  if (item.taskStatus === 'CANCELLED') return 'border-border bg-muted/60 text-muted-foreground'
  if (item.taskCategory === 'PRAZO_PROCESSUAL') return 'border-primary/60 bg-card text-foreground'
  return 'border-border bg-card text-foreground'
}

/**
 * CalendarTaskCard Component
 * ⭐ Dashed border + distinct colors = TASK (delivery/deadline)
 */
export const CalendarTaskCard: React.FC<CalendarTaskCardProps> = ({
  item,
  onClick,
  className = ''
}) => {
  const toneClasses = getTaskToneClasses(item)
  const overdue = isOverdue(item)

  return (
    <div
      onClick={onClick}
      className={cn(
        'min-w-0 cursor-pointer rounded-lg border-2 border-dashed p-2 transition-shadow hover:shadow-sm',
        toneClasses,
        className
      )}
    >
      <div className="mb-1 flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          {overdue ? (
            <AlertCircle className="size-4 shrink-0 text-destructive" />
          ) : item.taskCategory === 'DOCUMENTO' ? (
            <FileText className="size-4 shrink-0 text-muted-foreground" />
          ) : (
            <CheckSquare className="size-4 shrink-0 text-muted-foreground" />
          )}
          <span className="truncate text-sm font-medium">{item.title}</span>
        </div>
        <CalendarPriorityFlag priority={item.priority} />
      </div>

      {item.taskCategory && (
        <div className="mb-1">
          <Badge
            variant={item.taskCategory === 'PRAZO_PROCESSUAL' ? 'default' : 'secondary'}
            className="h-4 px-1.5 text-[10px]"
          >
            {taskCategoryLabels[item.taskCategory] ?? item.taskCategory}
          </Badge>
        </div>
      )}

      {overdue && (
        <div className="mb-1 flex items-center gap-1 text-xs font-semibold text-destructive">
          <Clock className="size-3" />
          <span>ATRASADA</span>
        </div>
      )}

      {(item.clientName || item.caseNumber) && (
        <div className="mt-1 border-t pt-1">
          <div className="truncate text-[11px] text-muted-foreground">
            {item.clientName && <span>{item.clientName}</span>}
            {item.clientName && item.caseNumber && <span className="mx-1">•</span>}
            {item.caseNumber && <CopyableProcessNumber number={item.caseNumber} className="font-mono" />}
          </div>
        </div>
      )}

      {item.taskStatus && (
        <div className="mt-1">
          <Badge variant="outline" className="h-4 px-1.5 text-[10px]">
            {taskStatusLabels[item.taskStatus] ?? item.taskStatus}
          </Badge>
        </div>
      )}

      {item.linkedEventId && (
        <div className="mt-1 text-[10px] text-muted-foreground">🔗 Vinculada a evento</div>
      )}
    </div>
  )
}
